<template>
  <div
    :key="key"
    class="mt-3"
  >
    <TopSection
      class="mb-3"
      :title="$t('claimWorkspace') | capitalize"
      :count="count"
    >
      <template v-slot:filters>
        <div class="flex items-center p-2">
          <b-button
            v-b-modal="'claimsModal'"
            size="sm"
            class="ml-4 medium-bold"
            variant="primary"
            @click="setMode('create')"
          >
            {{ $t('claimAll') | capitalize }}
          </b-button>
          <b-button
            v-b-modal="'claimsModal'"
            size="sm"
            class="ml-4 medium-bold"
            variant="primary"
            @click="setMode('delete')"
          >
            {{ $t('deleteClaims') | capitalize }}
          </b-button>
          <b-button
            v-b-modal="'removeAllClaimsConfirmationModal'"
            size="sm"
            class="ml-4 medium-bold"
            variant="danger"
          >
            {{ $t('remove_all') | capitalize }}
          </b-button>
          <b-modal
            id="removeAllClaimsConfirmationModal"
            ref="removeAllClaimsConfirmationModal"
            hide-footer
            hide-header
            size="xl"
          >
            <div class="claims-modal-body">
              {{ $t('removeAllClaimsQuestion') }}
            </div>
            <div class="flex justify-end">
              <span
                class="btn btn-danger mr-4"
                @click="closeModalAndRemove($refs.removeAllClaimsConfirmationModal,(true))"
              >{{ $t('yes') | upper }}</span>

              <button
                class="btn btn-primary"
                @click="closeModal($refs.removeAllClaimsConfirmationModal)"
              >
                {{ $t('no') | upper }}
              </button>
            </div>
          </b-modal>
        </div>
      </template>
    </TopSection>

    <!-- TABLE -->
    <v-client-table
      ref="selectedSoundRecordingsTable"
      :data="claimList"
      :columns="soundRecordingColumns"
      :options="options"
    >
      <!-- TITLE COLUMN -->
      <template v-slot:title="{ row }">
        <div class="table-header">
          {{ row.title | capitalize }}
        </div>
        <div class="mb-1">
          <div
            v-for="metadata in getMetadata(row, 'title')"
            :key="metadata"
            class="table-meta mb-2"
          >
            {{ metadata }}
          </div>
        </div>
      </template>

      <!-- ARTIST COLUMN -->
      <template v-slot:artist="{ row }">
        <div class="table-header">
          {{ row.artist | capitalize }}
        </div>
        <div class="mb-1">
          <div
            v-for="metadata in getMetadata(row,'artist')"
            :key="metadata"
            class="table-meta mb-2"
          >
            {{ metadata }}
          </div>
        </div>
      </template>

      <!-- ISRC COLUMN -->
      <template v-slot:isrc="{ row }">
        <div class="table-main-class">
          {{ row.isrc || '-' | upper }}
        </div>
      </template>

      <!-- DURATION COLUMN-->
      <template v-slot:duration="{ row }">
        {{ row.duration | normalizeSeconds }}
      </template>

      <!-- TYPE COLUMN-->
      <template v-slot:type="{ row }">
        {{ row.type }}
      </template>

      <!-- COUNTRIES COLUMN-->
      <template v-slot:fixation_country="{ row }">
        {{ row.fixation_country | get("name") }}
      </template>

      <!-- RELEASE NAMES COLUMN-->
      <template v-slot:releaseNames="{ row }">
        <ReleaseNames
          class-names="table-meta mb-2"
          :releases="getMetadata(row,'release')"
        />
      </template>

      <!-- ACTIONS COLUMN -->
      <template v-slot:actions="{ row }">
        <div class="d-flex align-items-center justify-content-end">
          <i
            v-b-tooltip.hover
            class="fa fa-trash clickable mr-4"
            :title="$t('remove') | capitalize"
            @click="removeSelected(row.id)"
          />
          <i
            class="fas fa-eye clickable"
            @click="navigateToSR(row.id)"
            :title="$t('view') | capitalize"
          />
        </div>
      </template>
    </v-client-table>

    <ClaimsModal
      ref="ClaimsModal"
      :sound-recordings="claimList"
      :mode="mode"
      :rightOwners="rightOwners"
      @close="closeModalAndRemove"
    >
      <template v-slot:details>
        <div class="table-header">
          {{ `${$t('claiming')} ${claimList.length} ${$t('soundRecordings')}`| capitalize }}
        </div>
      </template>
    </ClaimsModal>
  </div>
</template>

<style lang="scss">
.claims-modal-body {
  padding: 20px;
  border-bottom: 1px solid lightgray;
  border-top: 1px solid lightgray;
  margin-bottom: 10px;
}
</style>

<script>
import { capitalize } from "lodash"
import { mapGetters } from "vuex"

export default {
  name: "SelectedSoundRecordingsTable",
  data () {
    return {
      count: 0,
      key: 0,
      claimList: [],
      rightOwners: [],
      claimsModalOpen: false,
      mode: "create",
      soundRecordingColumns: [
        "title",
        "artist",
        "isrc",
        "duration",
        "type",
        "fixation_country",
        "releaseNames",
        "actions"
      ],
      options: {
        sortable: ["title", "artist", "isrc", "duration", "status"],
        headings: {
          title: capitalize(this.$t("title")),
          artist: capitalize(this.$t("artist")),
          isrc: capitalize(this.$t("isrc")),
          duration: capitalize(this.$t("duration")),
          type: capitalize(this.$t("type")),
          fixation_country: capitalize(this.$t("fixation_country")),
          releaseNames: capitalize(this.$t("releaseNames")),
          actions: ""
        },
      }
    }
  },
  created () {
    const currentClaimList = localStorage.getItem("claimList")
    if (currentClaimList) {
      const parsedClaimList = JSON.parse(currentClaimList)
      this.count = parsedClaimList.length
      this.claimList = parsedClaimList
      this.rightOwners = parsedClaimList.map(x=>x.right_owners).flat()
    }

  },
  computed: {
    ...mapGetters("soundsys", ["srTypes"])
  },
  methods: {
    closeModal (modal) {
      this.$nextTick(() => {
        // Wrapped in $nextTick to ensure DOM is rendered before closing
        modal.hide()
      })
    },
    closeModalAndRemove (modal, claimed) {
      if (claimed) {
        this.claimList = []
        localStorage.removeItem("claimList")
      }
      this.$nextTick(() => {
        // Wrapped in $nextTick to ensure DOM is rendered before closing
        modal.hide()
      })
    },
    getMetadata ({ extras }, tag) {
      if (extras) {
        return extras.reduce((current, next) => {
          if (next.tag === tag) {
            current.push(next.value)
          }
          return current
        }, [])
      }
      return []
    },
    setMode (mode) {
      this.mode = mode
    },
    removeSelected (id) {
      const indexToRemove = this.claimList.findIndex(item => item.id === id)
      this.claimList.splice(indexToRemove, 1)
      this.selectedSoundRecordings = {}
      localStorage.setItem("claimList", JSON.stringify(this.claimList))

      this.key = this.key + 1
    },
    navigateToSR (id) {
      this.$router.push({
        name: "sound-recording-from-db-detail",
        params: {
          id
        }
      })
    },
  }
}
</script>
